<template>
  <div id="cart" class="align-left">
    <p href="javascript:void(0)" class="closebtn pointer" @click="$emit('close')">×</p>
    <h4 class="align-center">{{ $t('cart.title') }}</h4>

    <div v-if="orders && orders.length <= 0" class="order-empty">
      <div class="content">
        <div>
          {{isSessionActive ? $t('cart.empty') : $t('cart.loginFirst')}}
          <br>
          <b-btn
            class="primary-button"
            v-if="!isSessionActive"
            @click="$router.push('/login')"
          >
            {{ $t('login') }}
          </b-btn>
        </div>
      </div>
    </div>
    <ul class="orders">
      <li v-for="(item, itemIndex) in orders" v-bind:key="itemIndex">
        <b-row>
          <!-- Checkbox, product image and optional span -->
          <b-col cols="4" style="padding-right: 0">
            <div class="position-relative">
              <!-- Not available span -->
              <span v-if="!isItemAvailableForPurchase(item)" class="not-available">
                {{ $t('notAvailable') }}
              </span>

              <!-- Checkbox and img -->
              <div :class="{'mask': !isItemAvailableForPurchase(item)}">
                <b-form-checkbox
                  :id="`checkbox-${itemIndex}`"
                  v-model="item.selectedForCheckout"
                  :name="`checkbox-${itemIndex}`"
                  :value="true"
                  :unchecked-value="false"
                  :disabled="!isItemAvailableForPurchase(item)"
                  class="checkbox-item"
                  @change="changeItemSelectedStatus(item, itemIndex)"
                />
                <img
                  :src="item.product.thumbnailUrls[0]"
                  alt="broken"
                  class="cart-img order-desc"
                  @click="gotoProduct(item.product)"
                >
              </div>
            </div>
          </b-col>

          <!-- Item details -->
          <b-col cols="8">
            <div class="order-desc" :class="{'mask': !isItemAvailableForPurchase(item)}">
              <!-- Item name & price -->
              <strong>
                {{item.product.name}}
                <br>
                <span class="font-weight-bold">
                  {{ $n(item.product.marked_price.amount, 'currency') }}
                </span>
                <br>
              </strong>
              <!-- Customizations -->
              <div style="font-size: 12px; margin-top: 5px; ">
                <span
                  v-for="(custom, cid) of item.customizations"
                  v-bind:key="cid"
                >{{custom | customDisplay}} |</span>
              </div>
              <!-- Counts -->
              <div>
                <b-form-select
                  size="sm"
                  v-model="item.counts"
                  :options="countOptions"
                  @change.native="updateCartItem(item)"
                  class="my-2"
                  style="max-width: 100px"
                />
              </div>
            </div>
            <!-- Delete button -->
            <div class="delete" @click="deleteSelected(item)">
              {{ $t('delete') }}
            </div>
          </b-col>
        </b-row>
      </li>
    </ul>

    <div class="total-line align-center bottom-action" v-if="orders && orders.length > 0">
      <b-row v-if="hasItemsSelectedForCheckout">
        <!-- <hr> -->
        <b-col cols="8" class="align-left">
          <strong>{{ $tc('cart.subtotal', totalItems) }}</strong>
        </b-col>
        <b-col class="align-right">
          <strong>{{ $n(subtotal.amount, 'currency')}}</strong>
        </b-col>
      </b-row>
      <br>
      <p class="info align-center">{{ $t('cart.finalCost') }}</p>
      <b-btn class="addToCart" @click="gotoCheckout()">{{ $t('cart.checkout') }}</b-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import notification from '@/services/notificationService';

export default {
  name: 'CartView',
  props: {
    sidebarWidth: {
      type: Number,
      required: false,
      default: 350,
    },
  },

  data() {
    return {
      countOptions: [],
    };
  },

  created() {
    this.countOptions = Array.from(Array(200).keys(), val => val + 1);
  },

  filters: {
    customDisplay(val) {
      return val.indexOf('|') >= 0 ? val.split('|')[0] : val;
    },
  },

  computed: {
    ...mapGetters({
      orders: 'cartStore/getCart',
      totalItems: 'cartStore/getTotalSelectedItems',
      subtotal: 'cartStore/getSubTotal',
      isSessionActive: 'authStore/isSessionActive',
    }),

    // Verify if item's product is available for purchase
    isItemAvailableForPurchase() {
      return item => {
        return (item.product.active && item.product.published && !item.product.fixesRequired);
      }
    },

    hasItemsSelectedForCheckout() {
      // Verifies if there's at least one item selected for checkout
      return this.orders.some(item => item.selectedForCheckout);
    }
  },

  methods: {
    gotoCheckout() {
      if (this.orders && this.orders.length > 0) {
        this.$router.push('/checkout');
        this.$emit('close');
      }
    },
    async gotoProduct(pid) {
      if (!pid) return;
      // await this.done();
      this.$router.push(`/products/${pid._id}`);
    },

    async updateCartItem(item) {
      this.$nextTick(async () => {
        if (item.counts > 0) {
          try {
            await this.$store.dispatch('cartStore/updateOrders', [
              item,
            ]);
            notification.success(
              this,
              this.$t('cart.updatedMsg'),
            );
          } catch (err) {
            console.log('Error', err);
            notification.error(
              this,
              this.$t('cart.cantUpdateMsg'),
            );
          }
        }
      });
    },

    async changeItemSelectedStatus(item, idx) {
      try {
        // If item's product is not available for purchase revert changes & ends function
        if(!this.isItemAvailableForPurchase(item)) {
          // Revert the item's selected status to its previous value
          this.orders[idx].selectedForCheckout = item.selectedForCheckout ? false : true;
          return;
        }

        // Wait for vue recalculate
        await this.$nextTick();

        // API call to change status
        await this.$store.dispatch(
          'cartStore/changeItemSelectedStatus',
          {
            _id: item._id,
            product: item.product._id,
            selectedForCheckout: item.selectedForCheckout
          }
        );
      }
      catch (error) {
        console.log(error);
        notification.error(
          this,
          this.$t('cart.cantUpdateMsg'),
        );

        // Revert the item's selected status to its previous value
        this.orders[idx].selectedForCheckout = item.selectedForCheckout ? false : true;
      }
    },

    async deleteSelected(item) {
      try {
        await this.$store.dispatch('cartStore/deleteOrders', [item]);
      } catch (err) {
        console.log(err);
        notification.error(
          this,
          this.$t('cart.cantUpdateMsg'),
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#cart {
  padding: 0rem 0.5rem;
  h4 {
    margin-bottom: 2rem;
  }

  .total-line {
    padding: 0.5rem 1rem;
  }

  .bottom-action {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 25vh;
    padding-top: 2rem;
    padding-bottom: 2rem;
    background-color: darken(white, 5%);
  }

  .order-empty {
    height: 500px;
    line-height: 500px;
    color: #bdbdbd;
    font-size: 1.5em;
    text-align: center;

    .content {
      display: inline-block;
      vertical-align: middle;
      line-height: normal;
    }
  }
}
.orders {
  list-style-type: none;
  padding: 0px;
  width: 100%;
  max-height: 60vh;
  overflow: auto;

  .order-desc {
    cursor: pointer;
  }

  .checkbox-item {
    display: inline !important;
    padding-top: 20px;
  }

  li {
    padding: 10px;
    margin-bottom: 10px;
    width: 100%;
  }

  span {
    padding: 1rem 0px;
  }

  .cart-img {
    width: auto;
    height: 70px;
  }

  .mask {
    opacity: 0.35;
    pointer-events: none;
    cursor: not-allowed;
  }

  .not-available {
    position: absolute;
    top: 5px;
    left: 5px;
    background-color: rgba(255, 0, 0, 0.85);
    color: white;
    padding: 2px 5px;
    font-size: 12px;
    border-radius: 3px;
    font-weight: bold;
    z-index: 1;
  }
}
</style>
